/**
 * Compose-time notify channel helpers.
 * Mirrors keyon/lib/NotificationPreference.php: Email / SMS / Phone / All.
 * "All" is a real picker value (and may be sent as an override); the backend
 * expands it to email + SMS + phone the same way as communication_preference.
 */

/**
 * Whether a message send may ask for its recipients to be alerted by e-mail,
 * SMS or phone (main #322: the "Notify recipients" box and "Select channels").
 *
 * On: the message routes take `notify: {enabled, channels}` (pacmny-api
 * MessageNotifier, NT-08), and the bridge maps main's `notify_recipients` and
 * `notify_channels` onto it. The recipients are the message's own; each gets
 * their own channel, and a sender's choice counts only among the channels that
 * recipient has enabled. The widgets offer the box only while this is true;
 * switched off again, the bridge refuses a send that still asks
 * (MESSAGE_NOTIFY_UNAVAILABLE_TEXT) rather than drop the alert unseen.
 */
export const MESSAGE_NOTIFY_AVAILABLE = true;

/** What a send that asked to notify is told while MESSAGE_NOTIFY_AVAILABLE is off. */
export const MESSAGE_NOTIFY_UNAVAILABLE_TEXT =
  "Notifying recipients by e-mail, SMS or phone is not available yet, so this message was not sent. " +
  "Send it without the notification; recipients will see it in their inbox.";

export const NOTIFY_CHANNEL_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "phone", label: "Phone" },
  { value: "all", label: "All" },
];

/** Default dropdown value from account_user.communication_preference. */
export function preferredNotifyChannel(communicationPreference) {
  const pref = String(communicationPreference || "")
    .toLowerCase()
    .trim();
  if (pref === "sms" || pref === "text") return "sms";
  if (pref === "phone" || pref === "call" || pref === "voice") return "phone";
  if (pref === "all") return "all";
  // email | empty | unknown → email
  return "email";
}

export function normalizeNotifyChannel(channel) {
  const ch = String(channel || "")
    .toLowerCase()
    .trim();
  if (ch === "sms" || ch === "email" || ch === "phone" || ch === "all") return ch;
  return "email";
}

/**
 * Build { uniqueId: channel } for recipients using saved overrides when present,
 * else preferred defaults from prefsById.
 */
export function buildNotifyChannelMap(recipients, overrides, prefsById) {
  const map = {};
  const list = Array.isArray(recipients) ? recipients : [];
  for (const r of list) {
    const uid = String(r.subaccountnumber || r.id || "").trim();
    if (!uid) continue;
    if (overrides && overrides[uid]) {
      map[uid] = normalizeNotifyChannel(overrides[uid]);
      continue;
    }
    const pref = prefsById && prefsById[uid] != null ? prefsById[uid] : "";
    map[uid] = preferredNotifyChannel(pref);
  }
  return map;
}
